import { Button, H2 } from '@blueprintjs/core';
import * as React from 'react';
import { useMutation, useQuery } from 'react-query';
import { Icon, PortalLogo } from 'vet-bones/design-system/components';

import { Loading } from 'src/ui/components/Loading';
import {
  VERIFY_CHALLENGE_BACK,
  VERIFY_CHALLENGE_CODE_INPUT,
  VERIFY_CHALLENGE_RESEND,
  VERIFY_CHALLENGE_SUBMIT,
} from 'src/ui/constants/testIds';
import { components } from 'src/ui/templates/copy';
import { redirectToUrl } from 'src/ui/utils/window';

const copy = components.verifyChallenge;

interface ChallengeStatus {
  email: string;
  organization: string;
}

export const VerifyChallengePage: React.VFC = () => {
  const [code, setCode] = React.useState('');

  const status = useQuery<ChallengeStatus>(
    'challenge-status',
    async () => {
      const res = await fetch('/oauth/verify-challenge/status');
      if (!res.ok) throw new Error('No challenge');
      return res.json();
    },
    {
      retry: false,
      onError: () => redirectToUrl('/login'),
    }
  );

  const verify = useMutation(
    async (verifyCode: string) => {
      const res = await fetch('/oauth/verify-challenge', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: verifyCode }),
      }).catch(() => {
        throw new Error(copy.unexpectedError);
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || copy.defaultVerifyError);
      }
      return data;
    },
    {
      onSuccess: (data) => {
        redirectToUrl(data.redirect);
      },
    }
  );

  const resend = useMutation(
    async () => {
      const res = await fetch('/oauth/verify-challenge/resend', {
        method: 'POST',
      }).catch(() => {
        throw new Error(copy.unexpectedResendError);
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || copy.defaultResendError);
      }
      return data;
    },
    {
      onSuccess: () => {
        setCode('');
        verify.reset();
      },
    }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    resend.reset();
    verify.mutate(code);
  };

  const handleResend = () => {
    verify.reset();
    resend.mutate();
  };

  const error = verify.error || resend.error;

  if (status.isLoading || !status.data) {
    return <Loading />;
  }

  return (
    <div className="sd-auth-card">
      <div className="sd-auth-card__card">
        <div className="sd-auth-card__header">
          <Icon IconComponent={PortalLogo} size={40} />
          <span>STARDOG</span>
        </div>

        <div className="sd-auth-card__body">
          <H2 className="sd-auth-card__title">{copy.title}</H2>
          <p className="sd-verify-challenge__subtitle">
            {copy.subtitle(status.data.email, status.data.organization)}
          </p>

          {verify.isSuccess ? (
            <Loading />
          ) : (
            <>
              {error ? (
                <p className="sd-auth-card__error">
                  {error instanceof Error
                    ? error.message
                    : copy.unexpectedError}
                </p>
              ) : null}

              {resend.isSuccess ? (
                <p className="sd-verify-challenge__success">
                  {copy.resendSuccess}
                </p>
              ) : null}

              <form onSubmit={handleSubmit}>
                <input
                  className="sd-auth-card__input sd-verify-challenge__code-input"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder={copy.codePlaceholder}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  maxLength={8}
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                  disabled={verify.isLoading}
                  data-testid={VERIFY_CHALLENGE_CODE_INPUT}
                />
                <Button
                  fill
                  type="submit"
                  loading={verify.isLoading}
                  disabled={code.length !== 8 || verify.isLoading}
                  text={copy.submitButton}
                  data-testid={VERIFY_CHALLENGE_SUBMIT}
                />
              </form>

              <Button
                minimal
                fill
                onClick={handleResend}
                loading={resend.isLoading}
                disabled={resend.isLoading}
                text={copy.resendButton}
                data-testid={VERIFY_CHALLENGE_RESEND}
              />
              <Button
                minimal
                fill
                onClick={() => redirectToUrl('/login')}
                text={copy.backButton}
                data-testid={VERIFY_CHALLENGE_BACK}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};
